import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LanguageService } from '../../../../../../projects/common/src/app/services/parameters/language/language.service';
import { DateTimePresentationPipe } from '../../../core/pipes/date-time-presentation.pipe';
import { UserDataService } from '../../../core/services/parameter/user-data/user-data.service';
import {
  IExtractionPointDetailStatistics,
  IExtractionPointDetailTable,
  IExtractionPointModal,
} from '../../../widget/interfaces/production-by-street';

import { CacheService } from '../../../../../../projects/common/src/app/services/cache.service';
import { UrlBuilderService } from 'projects/communication/src/app/services/url-builder.service';

@Injectable({
  providedIn: 'root',
})
export class ProductionByStreetService {
  private baseUrl: string;

  constructor(
    protected httpSvc: HttpClient,
    private userData: UserDataService,
    private langService: LanguageService,
    private dateFormatPipe: DateTimePresentationPipe,
    private cacheService: CacheService,
    private urlBuilder: UrlBuilderService,
  ) {
    this.baseUrl = this.urlBuilder.apiUrl;
  }

  /**
   * Function to get production by street zone from following params
   *
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  public getExtractionPointDetailByStreet(
    inputDate: string,
    shiftId: number,
    streetId: number,
    resultType: number,
  ): Observable<IExtractionPointModal> {
    const params = this.setParams(inputDate, shiftId, streetId.toString(), resultType);
    return this.httpSvc.get<any>(`${this.baseUrl}/Production/GetLhdScoopProductionDetailByStreet`, { params }).pipe(
      map((resp) => {
        if (resp.data) {
          const { data } = resp;
          const tableMapped = this.pointExtractionTableMapper(data.productionByStreetStatistics);
          data.productionByStreetStatistics = tableMapped;

          return data as IExtractionPointModal;
        }
      }),
    );
  }

  /**
   * Function map table for successfull column header names
   */
  private pointExtractionTableMapper(productionByStreetStatistics: IExtractionPointDetailStatistics[]): IExtractionPointDetailTable[] {
    const tableMapped = [];

    for (const point of productionByStreetStatistics) {
      const formattedDate = this.dateFormatPipe.transform(point.lastReport);
      const pointMapped = {
        ditch: point.zoneName,
        status: point.statusName,
        condition: point.condition,
        grade: point.grade === null ? '-' : `${Number(point.grade).toFixed(2)}%`,
        lastReport: formattedDate === 'Invalid date' ? '-' : formattedDate,
        lastOperator: point.operatorName ? point.operatorName : this.langService.message('unregistered-operator'),
        totalDone: point.done === 0 || !point.done ? '0' : point.done.toString(),
        scheduled: point.scheduled === 0 || !point.scheduled ? '0' : point.scheduled.toString(),
        notScheduledDoneLoops:
          point.notScheduledDoneLoops === 0 || !point.notScheduledDoneLoops ? '0' : point.notScheduledDoneLoops.toString(),
        remainder: point.remainder === 0 || !point.remainder ? '0' : point.remainder.toString(),
        compliance: point.compliance === 0 ? '0%' : `${point.compliance}%`,
      } as IExtractionPointDetailTable;

      tableMapped.push(pointMapped);
    }

    return tableMapped;
  }

  /**
   * Function to set params to build query
   *
   * @param inputDate Input date to get loops
   * @param shiftId Shift ID to get loops
   */
  private setParams(inputDate: string, shiftId: number, streetId?: string, resultType?: number): HttpParams {
    const { zoneId } = this.cacheService.session('selectedZone');
    let paramsObj = {};

    if (!streetId) {
      paramsObj = {
        fromObject: {
          inputDate,
          shiftId: shiftId.toString(),
          companyId: this.getUserCompany(),
          zoneId,
          resultType,
        },
      };
    } else {
      paramsObj = {
        fromObject: {
          inputDate,
          shiftId: shiftId.toString(),
          companyId: this.getUserCompany(),
          streetId,
          resultType,
        },
      };
    }

    return new HttpParams(paramsObj);
  }

  /**
   * Function to get companyId from current user
   */
  public getUserCompany(): string {
    return (this.userData.company() ? this.userData.company().id : 0).toString();
  }
}
